/**
 * CitationViewer
 *
 * A reusable dialog component that displays a list of evidence/citation sources for a matter.
 * It renders a trigger button (compact icon-only or text) that opens a modal dialog containing
 * an optional header area and a scrollable list of evidence items. Each evidence item is
 * rendered with `EvidenceCard` by default, but callers can override the rendering via
 * `renderEvidence`. The component also supports loading and error states, and allows callers
 * to append custom actions to the dialog footer via `additionalActions`.
 *
 * Typical usage:
 * ```tsx
 * <CitationViewer
 *   evidence={evidenceList}
 *   matterId={matterId}
 *   dialogTitle="Sources"
 *   triggerText="View Sources"
 * />
 * ```
 */
import { Eye } from 'lucide-react';
import React, { ReactNode, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

import { EvidenceCard, EvidenceData } from './EvidenceCard';

/**
 * Props for `CitationViewer`.
 *
 * - `evidence`: The list of evidence items to display. When empty and not loading, the component renders null.
 * - `matterId`: The matter identifier used by `EvidenceCard` for downstream data fetching.
 * - `dialogTitle`: Title text shown at the top of the dialog.
 * - `triggerText`: Optional label for the trigger button. Defaults to "View Sources ({count})".
 * - `compactTrigger`: When true, renders a compact icon-only trigger button.
 * - `isLoading`: When true, shows skeleton placeholders instead of content.
 * - `error`: When true (and not loading), displays an error message.
 * - `errorMessage`: Customizable error message when `error` is true.
 * - `buttonVariant`: Variant for the trigger button.
 * - `buttonSize`: Size for the trigger button.
 * - `buttonClassName`: Additional class names for the trigger button.
 * - `additionalActions`: Optional actions rendered in the dialog footer (e.g., Copy, Download).
 * - `renderHeader`: Optional renderer for custom content at the top of the dialog body.
 * - `renderEvidence`: Optional renderer for each evidence item; defaults to `EvidenceCard` per item.
 */
export interface CitationViewerProps {
  /** Evidence items to display */
  evidence: EvidenceData[];
  /** Matter ID for API calls */
  matterId: string;
  /** Title for the dialog */
  dialogTitle: string;
  /** Optional trigger button text - defaults to "View Sources ({count})" */
  triggerText?: string;
  /** Whether to show a compact trigger button with just an eye icon */
  compactTrigger?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Error state */
  error?: boolean;
  /** Custom error message */
  errorMessage?: string;
  /** Custom button variant */
  buttonVariant?: 'default' | 'ghost' | 'outline';
  /** Custom button size */
  buttonSize?: 'sm' | 'default' | 'lg';
  /** Custom button className */
  buttonClassName?: string;
  /** Additional actions to show in the dialog footer */
  additionalActions?: ReactNode;
  /** Render function for additional content above the evidence list */
  renderHeader?: () => ReactNode;
  /** Custom render function for each evidence item */
  renderEvidence?: (evidence: EvidenceData, index: number) => ReactNode;
}

/**
 * Displays a button that opens a dialog listing evidence sources with optional
 * header content, loading/error states, and customizable item rendering.
 */
export const CitationViewer: React.FC<CitationViewerProps> = ({
  evidence,
  matterId,
  dialogTitle,
  triggerText,
  compactTrigger = false,
  isLoading = false,
  error = false,
  errorMessage = 'Unable to load evidence sources',
  buttonVariant = 'ghost',
  buttonSize = 'sm',
  buttonClassName = '',
  additionalActions,
  renderHeader,
  renderEvidence,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const evidenceCount = evidence.length;
  const defaultTriggerText = `View Sources (${evidenceCount})`;

  // Don't render if no evidence
  if (!isLoading && evidenceCount === 0) {
    return null;
  }

  const handleOpenDialog = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setIsDialogOpen(true);
  };

  const defaultEvidenceRenderer = (
    evidenceItem: EvidenceData,
    index: number,
  ) => (
    <div key={evidenceItem.artifact_id || index} className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-medium">
          Source #{index + 1}
        </span>
      </div>
      <EvidenceCard evidence={evidenceItem} matterId={matterId} />
    </div>
  );

  return (
    <>
      {/* Trigger Button */}
      <Button
        size={buttonSize}
        variant={buttonVariant}
        onClick={handleOpenDialog}
        className={`${compactTrigger ? 'h-6 px-2' : ''} ${buttonClassName}`}
      >
        {compactTrigger ? (
          <Eye className="h-3 w-3" />
        ) : (
          <>
            <Eye className="h-3 w-3 mr-1" />
            {triggerText || defaultTriggerText}
          </>
        )}
      </Button>

      {/* Evidence Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {dialogTitle}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Optional header content */}
            {renderHeader && renderHeader()}

            {/* Loading State */}
            {isLoading && (
              <div className="space-y-4">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            )}

            {/* Error State */}
            {error && !isLoading && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                {errorMessage}
              </div>
            )}

            {/* Evidence List */}
            {!isLoading && !error && evidenceCount > 0 && (
              <div className="space-y-6">
                {evidence.map((evidenceItem, index) =>
                  renderEvidence
                    ? renderEvidence(evidenceItem, index)
                    : defaultEvidenceRenderer(evidenceItem, index),
                )}
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && evidenceCount === 0 && (
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded text-center">
                No evidence sources found
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Close
            </Button>
            {additionalActions}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
